import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import InputField from "../../Common/InputField";
import ToggleSwitch from "../../Common/ToggleSwitch";
import FormCard from "../../Common/FormCard";
import {
  getProjectData,
  getNextProjectCode,
  clearProjectCode,
  createUpdateProject,
} from "../../API_Service/Project_Slice";
import { showToast } from "../../Common/Toast";
import {
  sanitizeLabName,
  normalizeSpaces,
  Button,
  Icon,
  Spinner,
  Dropdown,
  hasPrivilege,
  PRIVILEGES,
} from "../../Common/UIComponents";
import TestTypeConfigCreate from "../ApplicationConfig/TestType/TestTypeConfigCreate";
import { saveTwoColumnListEntry } from "../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// FULL-SCREEN MODAL — hosts an embedded create page
// ─────────────────────────────────────────────
const InlineCreateModal = ({
  isOpen,
  onClose,
  children,
  maxWidth = "max-w-5xl",
}) => {
  if (!isOpen) return null;
  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[210] overflow-y-auto">
        <div className="min-h-full flex items-start justify-center p-4">
          <div
            className={`bg-[#f7fafa] rounded-2xl shadow-2xl w-full ${maxWidth} my-4 overflow-hidden relative`}
            style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
            <button
              onClick={onClose}
              title="Close"
              className="absolute top-4 right-4 z-10 h-9 w-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-[#08757C] hover:border-[#08757C]/40 transition-all"
            >
              <span className="material-symbols-outlined text-[18px]">
                close
              </span>
            </button>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// QUICK ADD TEST STANDARD MODAL
// Minimal popup: type field is locked to BASICSTANDARD, user only types the value.
// Calls the same saveTwoColumnListEntry API the DropdownConfigCreate page uses.
// ─────────────────────────────────────────────
const QuickAddStandardModal = ({
  isOpen,
  onClose,
  onCreated,
  companyId,
  employeeId,
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [apiErr, setApiErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValue("");
      setError("");
      setApiErr(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Test standard is required");
      return;
    }
    setSaving(true);
    setApiErr(null);
    try {
      const payload = {
        Term: "TESTSTANDARD",
        Value: normalizeSpaces(trimmed),
        ID: 0,
        CompanyID: companyId,
        CreatedBy: parseInt(employeeId) || 1,
      };
      const res = await dispatch(saveTwoColumnListEntry(payload));
      const data = res?.payload?.data ?? res?.payload;
      if (data === -1) {
        setApiErr("This test standard already exists.");
        return;
      }
      if (data !== undefined && data !== null) {
        onCreated?.(Number(data), trimmed);
        onClose();
      } else {
        setApiErr("Failed to save. Please try again.");
      }
    } catch {
      setApiErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
        >
          <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

          {/* Header */}
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ background: "linear-gradient(135deg,#08757C,#0a9aa3)" }}
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-[20px]">
                  verified
                </span>
              </div>
              <div>
                <h3 className="text-base font-black text-white">
                  Add Test Standard
                </h3>
                <p className="text-white/70 text-[11px]">
                  Quickly add a new standard
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">
                close
              </span>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {apiErr && (
              <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
                {apiErr}
              </div>
            )}

            {/* Dropdown type — locked */}
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
                Dropdown Type
              </label>
              <input
                type="text"
                value="Test Standard"
                disabled
                className="w-full px-3 py-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500 font-medium cursor-not-allowed"
              />
            </div>

            {/* Value */}
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
                Test Standard Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  setValue(sanitizeLabName(e.target.value));
                  setError("");
                }}
                placeholder="e.g. ISO 15189"
                maxLength={TEST_STANDARD_VALUE_MAX}
                autoFocus
                className={`w-full px-3 py-2.5 bg-slate-50 border rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-[#08757C]/20 focus:border-[#08757C] outline-none transition-all ${error ? "border-red-400" : "border-slate-200"}`}
              />
              <CharCounter value={value} max={TEST_STANDARD_VALUE_MAX} />
              {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner /> Saving…
                  </>
                ) : (
                  <>
                    <Icon name="save" className="text-[18px]" filled />
                    Save
                  </>
                )}
              </Button>
            </>
          </div>
        </div>
      </div>
    </>
  );
};

const PROJECT_NAME_MAX = 200;
const TEST_NAME_MAX = 500;
const TEST_STANDARD_VALUE_MAX = 50;
const TEST_HOURS_MAX = 6;
const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div
      className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}
    >
      <span>
        {len}/{max}
      </span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

const CreateEditProjectPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.CompanyId || localStorage.getItem("companyId");
  const employeeId = user?.EmployeeID || localStorage.getItem("empid");
  const isEditMode = id && id !== "create" && parseInt(id) > 0;
  const [codeLoading, setCodeLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const { testTypes, testStandards, loading } = useSelector(
    (state) => state.project,
  );

  const [formData, setFormData] = useState({
    projectName: "",
    projectCode: "",
    projectCodePrefix: "",
    projectCodeSuffix: "",
    isActive: true,
  });

  const [testDetails, setTestDetails] = useState([
    {
      id: 1,
      testTypeId: "",
      testTypeName: "",
      testName: "",
      testStandardId: "",
      testStandardName: "",
      basicStandardId: "",
      basicStandardName: "",
      testDescription: "",
      isReadonly: false,
      projectTestDetId: 0,
    },
  ]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fetchingStandards, setFetchingStandards] = useState(false);

  // Inline create modals — keep the project form values intact
  const [showAddStandardModal, setShowAddStandardModal] = useState(false);
  const [showAddTestTypeModal, setShowAddTestTypeModal] = useState(false);
  const [pendingAssignRowId, setPendingAssignRowId] = useState(null);
  const [pendingAssignField, setPendingAssignField] = useState(null); // "testStandard" | "testType"

  // After a new standard/test type is created, refresh the dropdown source
  // (re-call the same API the page uses on mount) and auto-select the new id
  // on the row that opened the modal.
  const refreshAndAssign = async (newId, newName = "") => {
    if (!pendingAssignRowId || !pendingAssignField || !newId) return;
    const rowId = pendingAssignRowId;
    const field = pendingAssignField;

    // Refresh the dropdown sources (testTypes / testStandards) in Redux
    await dispatch(getProjectData({ projectId: 0, companyId, testTypeId: 0 }));

    // Assign the new id (and clear basic standard when the test type changes,
    // so the auto-populated basic standard for the new type takes effect)
    setTestDetails((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? field === "testStandard"
            ? {
                ...item,
                testStandardId: Number(newId),
                testStandardName: newName || item.testStandardName,
              }
            : {
                ...item,
                testTypeId: Number(newId),
                testTypeName: newName || item.testTypeName,
                // Reset linked basic standard fields so the fresh fetch fills them
                basicStandardId: "",
                basicStandardName: "",
                testDescription: "",
                isReadonly: false,
              }
          : item,
      ),
    );

    // For a new Test Type: fetch basic standards for it so the row's
    // Basic Standard field auto-fills (same behaviour as picking from the list).
    if (field === "testType") {
      await fetchBasicStandards(Number(newId), rowId);
    }

    setPendingAssignRowId(null);
    setPendingAssignField(null);
  };

  const isInactiveEdit = isEditMode && !formData.isActive;

  // Convert API test types to options format
  const testTypeOptions = (testTypes || []).map((item) => ({
    value: Number(item.value),
    label: item.label,
  }));

  // Convert API test standards to options format
  const testStandardOptions = (testStandards || []).map((item) => ({
    value: Number(item.value),
    label: item.label,
  }));

  // Fetch project data for edit mode or dropdowns for create mode
  const fetchInitialData = async () => {
    setDataLoading(true);
    try {
      const projectId = isEditMode ? parseInt(id) : 0;
      const result = await dispatch(
        getProjectData({ projectId, companyId, testTypeId: 0 }),
      ).unwrap();

      console.log("API Result:", result);

      if (isEditMode && result.data) {
        // Populate project master data
        if (
          result.data.data.projectMaster &&
          result.data.data.projectMaster.length > 0
        ) {
          const projectMaster = result.data.data.projectMaster[0];

          setFormData({
            projectName: normalizeSpaces(projectMaster.ProjectName || ""),
            projectCode: projectMaster.ProjectCode || "",
            projectCodePrefix: projectMaster.Prefix || "",
            projectCodeSuffix: projectMaster.Suffix || "",
            isActive: projectMaster.Status === "AC",
          });
        }

        // Populate test details
        if (
          result.data.data.testDetails &&
          result.data.data.testDetails.length > 0
        ) {
          const mappedTestDetails = result.data.data.testDetails.map(
            (test, index) => {
              return {
                id: index + 1,
                testTypeId: test.TestTypeId ? Number(test.TestTypeId) : "",
                testTypeName: test.TestTypeName || "",
                testName: test.TestName || "",
                testStandardId: test.TestStandardId
                  ? Number(test.TestStandardId)
                  : "",
                testStandardName: test.TestStandardName || "",
                basicStandardId: test.BasicStandardId
                  ? Number(test.BasicStandardId)
                  : "",
                basicStandardName: test.BasicStandardName || "",
                testDescription: test.TestDescription || "",
                testHours:
                  test.TotalHours !== undefined && test.TotalHours !== null
                    ? String(test.TotalHours)
                    : "",
                isReadonly: test.BasicStandardId ? true : false,
                projectTestDetId: test.ProjectTestDetId || 0,
              };
            },
          );
          setTestDetails(mappedTestDetails);
        }
      }

      // Fetch project code for create mode
      if (!isEditMode) {
        await fetchProjectCode();
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      showToast("Failed to load data", "error");
    } finally {
      setDataLoading(false);
    }
  };

  const fetchProjectCode = async () => {
    setCodeLoading(true);
    try {
      const response = await dispatch(
        getNextProjectCode({ companyId }),
      ).unwrap();

      const fullCode = response?.projectCode || "";

      const prefixMatch = fullCode.match(/^[A-Za-z]+/);
      const suffixMatch = fullCode.match(/\d+$/);

      const prefix = prefixMatch ? prefixMatch[0] : "";
      const suffix = suffixMatch ? suffixMatch[0] : "";

      setFormData((prev) => ({
        ...prev,
        projectCode: fullCode,
        projectCodePrefix: prefix,
        projectCodeSuffix: suffix,
      }));
    } catch (error) {
      console.error("Error fetching project code:", error);
    } finally {
      setCodeLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchInitialData();
    }
  }, [companyId, isEditMode, id]);

  useEffect(() => {
    return () => {
      dispatch(clearProjectCode());
    };
  }, [dispatch]);

  // Fetch Basic Standards when Test Type changes
  const fetchBasicStandards = async (testTypeId, testId) => {
    if (!testTypeId) return;

    setFetchingStandards(true);
    try {
      const result = await dispatch(
        getProjectData({ projectId: 0, companyId, testTypeId }),
      ).unwrap();

      console.log("Basic Standards API Response:", result);

      // Extract basic standards from the correct path in the response
      let basicStandardsData = [];

      // Check different possible response structures
      if (result?.data?.data?.basicStandards) {
        basicStandardsData = result.data.data.basicStandards;
      } else if (result?.data?.basicStandards) {
        basicStandardsData = result.data.basicStandards;
      } else if (Array.isArray(result)) {
        basicStandardsData = result;
      }

      console.log("Extracted Basic Standards:", basicStandardsData);

      if (basicStandardsData && basicStandardsData.length > 0) {
        const firstStandard = basicStandardsData[0];

        // Handle different property names in the response
        const standardId =
          firstStandard.Basic_Standard_Id ||
          firstStandard.BasicStandardId ||
          firstStandard.value ||
          firstStandard.id;

        const standardName =
          firstStandard.Basic_Standard_Name ||
          firstStandard.BasicStandardName ||
          firstStandard.label ||
          firstStandard.name;

        const description = firstStandard.TestDescription || "";

        setTestDetails((prev) =>
          prev.map((item) =>
            item.id === testId
              ? {
                  ...item,
                  basicStandardId: standardId,
                  basicStandardName: standardName,
                  testDescription: description,
                  isReadonly: true,
                }
              : item,
          ),
        );

        // showToast(`Basic standard "${standardName}" auto-populated`, "success");
      } else {
        setTestDetails((prev) =>
          prev.map((item) =>
            item.id === testId
              ? {
                  ...item,
                  basicStandardId: "",
                  basicStandardName: "",
                  testDescription: "",
                  isReadonly: false,
                }
              : item,
          ),
        );
        showToast(
          "No predefined standards found. You can enter manually.",
          "info",
        );
      }
    } catch (error) {
      console.error("Error fetching basic standards:", error);
      setTestDetails((prev) =>
        prev.map((item) =>
          item.id === testId
            ? {
                ...item,
                isReadonly: false,
              }
            : item,
        ),
      );
      showToast("Failed to fetch basic standards", "error");
    } finally {
      setFetchingStandards(false);
    }
  };

  // Add new test row
  const handleAddTest = () => {
    const newId = testDetails.length + 1;
    setTestDetails([
      ...testDetails,
      {
        id: newId,
        testTypeId: "",
        testTypeName: "",
        testName: "",
        testStandardId: "",
        testStandardName: "",
        basicStandardId: "",
        basicStandardName: "",
        testDescription: "",
        testHours: "",
        isReadonly: false,
        projectTestDetId: 0,
      },
    ]);
  };

  // Remove test row
  const handleRemoveTest = (id) => {
    if (testDetails.length === 1) {
      showToast("At least one test detail is required", "warning");
      return;
    }
    setTestDetails(testDetails.filter((item) => item.id !== id));
  };

  // Handle test detail change
  const handleTestChange = async (id, field, value) => {
    const numericValue = value ? Number(value) : "";
    const selectedTestType = testTypeOptions.find(
      (opt) => opt.value === numericValue,
    );
    const selectedTestStandard = testStandardOptions.find(
      (opt) => opt.value === numericValue,
    );

    setTestDetails(
      testDetails.map((item) =>
        item.id === id
          ? {
              ...item,
              [field === "testType" ? "testTypeId" : "testStandardId"]:
                numericValue,
              [field === "testType" ? "testTypeName" : "testStandardName"]:
                field === "testType"
                  ? selectedTestType?.label || ""
                  : selectedTestStandard?.label || "",
              ...(field === "testType" && {
                basicStandardId: "",
                basicStandardName: "",
                isReadonly: false,
              }),
            }
          : item,
      ),
    );

    // Fetch basic standards when test type changes - UNCOMMENTED THIS
    if (field === "testType" && value) {
      const testTypeId = selectedTestType?.value || numericValue;
      await fetchBasicStandards(testTypeId, id);
    }
  };

  // Handle manual input for test name
  const handleTestNameChange = (id, value) => {
    setTestDetails(
      testDetails.map((item) =>
        item.id === id ? { ...item, testName: sanitizeLabName(value) } : item,
      ),
    );
  };

  const handleTestHoursChange = (id, value) => {
    const cleaned = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setTestDetails(
      testDetails.map((item) =>
        item.id === id ? { ...item, testHours: cleaned } : item,
      ),
    );
  };

  const handleInputChange = (value, e) => {
    const { name } = e.target;
    const val = name === "projectName" ? sanitizeLabName(value) : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
    if (name === "projectName") setSaveError(null);
  };

  const handleSaveProject = async () => {
    const hasTestData = testDetails.some(
      (test) =>
        test.testTypeId ||
        test.testName ||
        test.testStandardId ||
        test.basicStandardId,
    );

    setSaving(true);
    setSaveError(null);

    try {
      const testDetailsList = testDetails.map((test) => ({
        ProjectTestDetId: test.projectTestDetId || 0,
        TestName: normalizeSpaces(test.testName || ""),
        TestStandardId: parseInt(test.testStandardId) || 0,
        BasicStandardId: parseInt(test.basicStandardId) || 0,
        TestTypeId: parseInt(test.testTypeId) || 0,
        TotalHours: parseFloat(test.testHours) || 0,
      }));

      const projectData = {
        ProjectID: isEditMode ? id.toString() : "0",
        ProjectName: normalizeSpaces(formData.projectName),
        ProjectCode: formData.projectCode,
        ProjectCodePrefix: formData.projectCodePrefix || "",
        ProjectCodeSuffix: formData.projectCodeSuffix || "",
        SavedBy: employeeId,
        Status: formData.isActive ? "AC" : "DEL",
        TestDetails: testDetailsList,
      };

      const result = await dispatch(
        createUpdateProject({ projectData, companyId }),
      ).unwrap();

      const projectId = result?.ProjectID ?? result?.projectID ?? result;
      if (Number(projectId) === -1) {
        setSaveError(
          "A project with this name already exists. Please use a different name.",
        );
        return;
      }
      if (Number(projectId) === -2) {
        setSaveError(
          "This project is already mapped to a booking. It cannot be set to Inactive.",
        );
        return;
      }

      navigate("/project-management", {
        state: { refresh: true, editSuccess: true, isNew: !isEditMode },
      });
    } catch (error) {
      console.error("Failed to save project:", error);
      setSaveError(error?.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="px-8 py-4 flex-1 w-full">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-3 gap-4">
          <div>
            <nav className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              <span
                onClick={() => navigate("/project-management")}
                className="cursor-pointer hover:text-primary transition-colors"
              >
                Project Management
              </span>
              <span className="material-symbols-outlined text-[12px]">
                chevron_right
              </span>
              <span className="text-primary">
                {isEditMode ? "Edit Project" : "Create Project"}
              </span>
            </nav>
            <h2 className="text-3xl font-extrabold text-primary tracking-tight">
              {isEditMode ? "Edit Project" : "Create New Project"}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/project-management")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
            >
              <span className="material-symbols-outlined text-lg">
                arrow_back
              </span>
              Back to List
            </button>
            <Button variant="primary" onClick={handleSaveProject} disabled={saving}>
              <span className="material-symbols-outlined text-lg">
                {saving ? "hourglass_empty" : "save"}
              </span>
              {saving
                ? "Saving..."
                : isEditMode
                  ? "Update Project"
                  : "Save Project"}
            </Button>
          </div>
        </div>

        {/* Error Banner */}
        {saveError && (
          <div className="mb-4 bg-red-50 text-[#ba1a1a] rounded-xl px-5 py-4 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">error</span>
            {saveError}
          </div>
        )}

        {/* Inactive project lock banner */}
        {isInactiveEdit && (
          <div className="mb-4 bg-amber-50 text-amber-800 rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-amber-100">
            <span className="material-symbols-outlined text-[20px]">lock</span>
            <span>
              This project is <strong>Inactive</strong>. Only the status can be
              changed. Activate the project to edit other details.
            </span>
          </div>
        )}

        {/* Form Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-12 lg:col-span-8 space-y-3">
            {/* Project Identity Card */}
            <FormCard icon="assignment" title="Project Identity">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Project Name"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                    icon="description"
                    maxLength={PROJECT_NAME_MAX}
                    disabled={isInactiveEdit}
                  />
                  <CharCounter
                    value={formData.projectName}
                    max={PROJECT_NAME_MAX}
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Project Code"
                    name="projectCode"
                    value={formData.projectCode}
                    onChange={handleInputChange}
                    placeholder="Enter project code"
                    icon="code"
                    disabled={true}
                    maxLength={20}
                  />
                  {codeLoading && (
                    <p className="text-xs text-blue-500 mt-1">
                      Generating project code...
                    </p>
                  )}
                  {!isEditMode && formData.projectCode && !codeLoading && (
                    <p className="text-xs text-green-500 mt-1">
                      ✓ Project code generated: {formData.projectCodePrefix} -{" "}
                      {formData.projectCodeSuffix}
                    </p>
                  )}
                </div>
              </div>
            </FormCard>

            {/* Test Details Card */}
            <FormCard
              icon="science"
              title="Test Details"
              headerRight={
                <button
                  type="button"
                  onClick={handleAddTest}
                  disabled={isInactiveEdit}
                  className="flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-[18px]">
                    add
                  </span>
                  Add Test
                </button>
              }
            >
              <div className="space-y-4">
                {/* Test Details Rows */}
                {testDetails.map((test, index) => (
                  <div
                    key={test.id}
                    className="border border-slate-200 rounded-lg p-4 relative"
                  >
                    {/* Remove Button — floats outside the card so it never overlaps with column controls */}
                    {index > 0 && !isInactiveEdit && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTest(test.id)}
                        title="Remove this test"
                        className="absolute -top-2 -right-2 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm text-red-500 hover:bg-red-50 hover:border-red-300 transition-all"
                      >
                        <span className="material-symbols-outlined text-[14px]">
                          close
                        </span>
                      </button>
                    )}

                    {/* Loading indicator for fetching standards */}
                    {fetchingStandards && test.testTypeId && (
                      <div className="absolute top-2 left-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {/* Test Standard */}
                      <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                            Test Standard
                          </label>
                          {hasPrivilege(PRIVILEGES.CONFIG_VIEW) && (
                            <button
                              type="button"
                              onClick={() => {
                                setPendingAssignRowId(test.id);
                                setPendingAssignField("testStandard");
                                setShowAddStandardModal(true);
                              }}
                              disabled={isInactiveEdit}
                              title="Add a new test standard without leaving this page"
                              className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="material-symbols-outlined text-[14px]">
                                add
                              </span>
                              Add New
                            </button>
                          )}
                        </div>
                        <Dropdown
                          options={testStandardOptions}
                          value={test.testStandardId}
                          onChange={(val) =>
                            handleTestChange(test.id, "testStandard", val)
                          }
                          placeholder="Select test standard..."
                          disabled={isInactiveEdit}
                        />
                      </div>

                      {/* Test Type */}
                      <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                            Test Type
                          </label>
                          {hasPrivilege(PRIVILEGES.CONFIG_VIEW) && (
                            <button
                              type="button"
                              onClick={() => {
                                setPendingAssignRowId(test.id);
                                setPendingAssignField("testType");
                                setShowAddTestTypeModal(true);
                              }}
                              disabled={isInactiveEdit}
                              title="Add a new test type without leaving this page"
                              className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="material-symbols-outlined text-[14px]">
                                add
                              </span>
                              Add New
                            </button>
                          )}
                        </div>
                        <Dropdown
                          options={testTypeOptions}
                          value={test.testTypeId}
                          onChange={(val) =>
                            handleTestChange(test.id, "testType", val)
                          }
                          placeholder="Select test type..."
                          disabled={isInactiveEdit}
                        />
                      </div>

                      {/* Test Name */}
                      <div className="col-span-2 md:col-span-1">
                        <InputField
                          label="Test Name"
                          value={test.testName}
                          onChange={(value) =>
                            handleTestNameChange(test.id, value)
                          }
                          placeholder="Enter test name"
                          icon="biotech"
                          maxLength={TEST_NAME_MAX}
                          disabled={isInactiveEdit}
                        />
                        <CharCounter
                          value={test.testName}
                          max={TEST_NAME_MAX}
                        />
                      </div>

                      {/* Basic Standard — auto-filled from Test Type, never typed manually */}
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                          Basic Standard
                        </label>
                        <div className="relative">
                          <InputField
                            value={test.basicStandardName}
                            onChange={() => {}}
                            placeholder="Auto-filled from Test Type"
                            icon="verified"
                            readOnly
                            disabled={isInactiveEdit}
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <span className="material-symbols-outlined text-slate-400 text-sm">
                              lock
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Total Hours */}
                      <div className="col-span-2 md:col-span-1">
                        <InputField
                          label="Total Hours"
                          value={test.testHours}
                          onChange={(value) =>
                            handleTestHoursChange(test.id, value)
                          }
                          placeholder="e.g. 8"
                          icon="schedule"
                          inputMode="decimal"
                          maxLength={TEST_HOURS_MAX}
                          disabled={isInactiveEdit}
                        />
                        <CharCounter
                          value={test.testHours}
                          max={TEST_HOURS_MAX}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </FormCard>
          </div>

          {/* Right Column */}
          <div className="col-span-12 lg:col-span-4 space-y-3">
            {/* Status Card */}
       {/*      <FormCard icon="toggle_on" title="Project Status">
              <ToggleSwitch
                label="System Status"
                subLabel="Active Visibility"
                checked={formData.isActive}
                onChange={(checked) =>
                  isEditMode &&
                  setFormData((prev) => ({ ...prev, isActive: checked }))
                }
                activeText="Active"
                inactiveText="Inactive"
                disabled={!isEditMode}
              />
            </FormCard>
             */}
            {/* Visual Helper Card */}
            <div className="relative overflow-hidden rounded-xl h-48 bg-primary p-6 shadow-lg">
              <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-black/10 rounded-full blur-2xl"></div>
              <div className="relative z-10 h-full flex flex-col justify-end">
                <h4 className="text-white font-bold text-sm mb-1">
                  Compliance Guide
                </h4>
                <p className="text-white/70 text-[11px] leading-snug">
                  Basic Standards are auto-populated based on selected Test
                  Type.
                </p>
              </div>
            </div>

            {/* Info Note */}
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-[11px] leading-relaxed text-amber-800">
                <span className="font-bold">Note:</span> Project Code is
                auto-generated and cannot be changed. Basic Standards are
                automatically loaded when you select a Test Type.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-slate-200">
          <button
            onClick={() => navigate("/project-management")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">
              arrow_back
            </span>
            Back to List
          </button>
          <Button variant="primary" onClick={handleSaveProject} disabled={saving}>
            <span className="material-symbols-outlined text-lg">
              {saving ? "hourglass_empty" : "save"}
            </span>
            {saving
              ? "Saving..."
              : isEditMode
                ? "Update Project"
                : "Save Project"}
          </Button>
        </div>
      </div>

      {/* ── Quick Add Test Standard — locked-type popup, one input ── */}
      <QuickAddStandardModal
        isOpen={showAddStandardModal}
        onClose={() => {
          setShowAddStandardModal(false);
          setPendingAssignRowId(null);
          setPendingAssignField(null);
        }}
        companyId={companyId}
        employeeId={employeeId}
        onCreated={(newId, newName) => {
          refreshAndAssign(newId, newName);
        }}
      />

      {/* ── Inline Add Test Type (full Test Type create page) ── */}
      <InlineCreateModal
        isOpen={showAddTestTypeModal}
        onClose={() => {
          setShowAddTestTypeModal(false);
          setPendingAssignRowId(null);
          setPendingAssignField(null);
        }}
      >
        <TestTypeConfigCreate
          embedded
          onCancel={() => {
            setShowAddTestTypeModal(false);
            setPendingAssignRowId(null);
            setPendingAssignField(null);
          }}
          onSuccess={(newId, newName) => {
            refreshAndAssign(newId, newName);
            setShowAddTestTypeModal(false);
          }}
        />
      </InlineCreateModal>
    </DashboardLayout>
  );
};

export default CreateEditProjectPage;
